import { useState } from 'react';
import type BackendService from '../../../services/admin/BackendService';
import { useCardVideoAssetPoll, stillProcessing } from './useCardVideoAssetPoll';
import { useCardVideoUploadPhaseCallbacks } from './useCardVideoUploadPhaseCallbacks';
import { errText } from './videoLibraryErrorText';

export const VIDEO_ACCEPT = 'video/mp4,video/quicktime,video/webm,video/x-matroska';

export type CardVideoUploadPhase =
  | 'idle'
  | 'awaiting-metadata'
  | 'uploading'
  | 'transcoding'
  | 'error';

/** A readable library title from a file name: no extension, separators as spaces. */
export function titleFromFilename(fileName: string): string {
  return fileName
    .replace(/\.[^.]+$/, '')
    .replace(/[_-]+/g, ' ')
    .trim();
}

export function isVideoFile(file: File): boolean {
  return file.type.startsWith('video/') || /\.(mp4|mov|webm|mkv)$/i.test(file.name);
}

/**
 * Per-card video upload + transcode poll. Mirrors useVideoLibrary's
 * interval-while-processing pattern: the timer only exists while the
 * attached asset is PENDING/PROCESSING, patches status fields, and
 * clears on unmount. onAttach fires as soon as the upload is queued —
 * draft autosave then owns the library reference, so navigating away
 * does not cancel the Cloud Run transcode job.
 */
export function useCardVideoUpload(opts: {
  fundOpportunityId: string;
  backendService: BackendService;
  attachedExternalId?: string;
  onAttach: (externalId: string) => void;
}) {
  const { fundOpportunityId, backendService, attachedExternalId, onAttach } = opts;
  const [phase, setPhase] = useState<CardVideoUploadPhase>('idle');
  const [stashedFile, setStashedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { onReady, onFailed, onProcessing } = useCardVideoUploadPhaseCallbacks(
    setPhase,
    setErrorMessage,
  );

  const { asset, applyAsset, startPoll, backendServiceRef } = useCardVideoAssetPoll({
    backendService,
    attachedExternalId,
    onReady,
    onFailed,
    onProcessing,
  });

  const beginUpload = (file: File) => {
    setStashedFile(file);
    setErrorMessage(null);
    setUploadProgress(0);
    setPhase('awaiting-metadata');
  };

  const cancelMetadataPrompt = () => {
    setStashedFile(null);
    setPhase('idle');
  };

  const confirmMetadata = async (title: string, description: string) => {
    if (!stashedFile) return;
    const file = stashedFile;
    setStashedFile(null);
    setPhase('uploading');
    setUploadProgress(0);
    try {
      const uploaded = await backendServiceRef.current.uploadVideoToLibrary(
        { publicTitle: title, publicDescription: description },
        fundOpportunityId,
        file,
        (progress) => setUploadProgress(progress),
      );
      onAttach(uploaded.externalId);
      applyAsset(uploaded);
      if (stillProcessing(uploaded.status)) startPoll(uploaded.externalId);
    } catch (err) {
      setPhase('error');
      setErrorMessage(errText(err));
    }
  };

  return {
    phase,
    stashedFile,
    uploadProgress,
    errorMessage,
    asset,
    beginUpload,
    confirmMetadata,
    cancelMetadataPrompt,
  };
}
